import { useMemo } from 'react';
import type { PlanDefinition, PlanFeatureEntry } from '@opinix-cloud/shared';

interface FeatureMatrixProps {
  /** Already in display order: STARTER, PRO, ENTERPRISE. */
  plans: PlanDefinition[];
}

/**
 * Live feature matrix rendered from `/api/plans`. Rows are features
 * grouped by category (one section header per group); columns are
 * plans. Each cell is a check or em-dash depending on
 * `PlanFeature.enabled`. When the public response uses the static
 * fallback (no live featureMatrix entries) the component renders
 * nothing rather than an empty/broken matrix.
 *
 * Marketing palette: light surface (white card on steel-50 section),
 * navy text, teal-600 for the check, slate-300 for the em-dash. No
 * purple/violet/dark surfaces here — the hero/CTA bookends carry the
 * navy treatment.
 */
export function FeatureMatrix({ plans }: FeatureMatrixProps): JSX.Element | null {
  const rows = useMemo(() => buildRows(plans), [plans]);

  // Empty state: when the API was unavailable and the static fallback
  // kicked in (no featureMatrix entries on any plan), don't render a
  // skeleton matrix — render nothing so the page below the cards still
  // looks intentional.
  if (rows.length === 0) return null;

  return (
    <section className="bg-white py-24 sm:py-32 border-t border-steel-200">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="eyebrow">Compare plans</p>
          <h2 className="mt-3 section-title">Side by side.</h2>
          <p className="mt-5 text-lg text-steel-600 leading-relaxed">
            The same platform powers every tier. What changes is which features
            are bundled with each plan &mdash; from training tools to fleet
            controls to support tier.
          </p>
        </div>

        <div className="mt-12 -mx-6 sm:mx-0 overflow-x-auto">
          <table className="min-w-full text-left border-separate border-spacing-0">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-white px-4 sm:px-6 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-steel-500 border-b border-steel-200"
                >
                  Feature
                </th>
                {plans.map((plan) => (
                  <th
                    key={plan.tier}
                    scope="col"
                    className={`px-4 sm:px-6 py-4 text-sm font-bold tracking-tight border-b border-steel-200 text-center ${
                      plan.tier === 'PRO'
                        ? 'text-cyan-600 bg-cyan-50/60'
                        : 'text-navy-900'
                    }`}
                  >
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) =>
                row.kind === 'header' ? (
                  <tr key={`h-${row.category}`}>
                    <th
                      scope="row"
                      colSpan={1 + plans.length}
                      className="sticky left-0 bg-steel-50/80 px-4 sm:px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-steel-500 border-b border-steel-100"
                    >
                      {prettyCategory(row.category)}
                    </th>
                  </tr>
                ) : (
                  <tr key={`f-${row.featureKey}`}>
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-white px-4 sm:px-6 py-4 text-sm font-medium text-navy-900 border-b border-steel-100"
                    >
                      <div>{row.featureName}</div>
                      {row.description && (
                        <div className="text-xs text-steel-500 mt-0.5 font-normal">
                          {row.description}
                        </div>
                      )}
                    </th>
                    {plans.map((plan) => {
                      const enabled = row.byPlan[plan.tier] ?? false;
                      return (
                        <td
                          key={plan.tier}
                          className={`px-4 sm:px-6 py-4 text-base text-center border-b border-steel-100 ${
                            plan.tier === 'PRO' ? 'bg-cyan-50/40' : ''
                          }`}
                        >
                          {enabled ? (
                            <span
                              className="text-teal-600 font-semibold"
                              aria-label={`${row.featureName} included in ${plan.name}`}
                            >
                              ✓
                            </span>
                          ) : (
                            <span
                              className="text-slate-300"
                              aria-label={`${row.featureName} not included in ${plan.name}`}
                            >
                              &mdash;
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Row construction
// ---------------------------------------------------------------------------

type FeatureRow =
  | { kind: 'header'; category: string }
  | {
      kind: 'feature';
      featureKey: string;
      featureName: string;
      description: string | null;
      category: string;
      byPlan: Record<string, boolean>;
    };

/**
 * Walks the per-plan featureMatrix arrays and pivots them into a single
 * list of rows ordered by category then feature name. Each feature row
 * carries a `byPlan` map so the renderer can drop a ✓ or em-dash per
 * column. Categories are emitted as section-header rows interleaved
 * with the feature rows.
 */
function buildRows(plans: PlanDefinition[]): FeatureRow[] {
  // Aggregate every (key, name, description, category) tuple seen
  // across plans. First-seen wins for the metadata since the backend
  // returns identical metadata for the same feature on every plan.
  type Acc = {
    name: string;
    description: string | null;
    category: string;
    byPlan: Record<string, boolean>;
  };
  const byKey = new Map<string, Acc>();

  for (const plan of plans) {
    const matrix: PlanFeatureEntry[] = plan.featureMatrix ?? [];
    for (const entry of matrix) {
      const acc = byKey.get(entry.key) ?? {
        name: entry.name,
        description: entry.description,
        category: entry.category,
        byPlan: {},
      };
      acc.byPlan[plan.tier] = entry.enabled;
      byKey.set(entry.key, acc);
    }
  }

  if (byKey.size === 0) return [];

  // Sort within each category by name for stability across page loads.
  const categories = new Map<string, Array<[string, Acc]>>();
  for (const [key, acc] of byKey) {
    const list = categories.get(acc.category) ?? [];
    list.push([key, acc]);
    categories.set(acc.category, list);
  }

  // Categories ordered using a fixed taxonomy first, then any unknown
  // category appended (so a superadmin who introduces a new bucket
  // doesn't have it disappear from the matrix).
  const KNOWN_ORDER = ['core', 'training', 'fleet', 'ops', 'support'];
  const orderedCats = [
    ...KNOWN_ORDER.filter((c) => categories.has(c)),
    ...[...categories.keys()].filter((c) => !KNOWN_ORDER.includes(c)).sort(),
  ];

  const rows: FeatureRow[] = [];
  for (const cat of orderedCats) {
    rows.push({ kind: 'header', category: cat });
    const list = (categories.get(cat) ?? []).sort((a, b) =>
      a[1].name.localeCompare(b[1].name),
    );
    for (const [key, acc] of list) {
      rows.push({
        kind: 'feature',
        featureKey: key,
        featureName: acc.name,
        description: acc.description,
        category: acc.category,
        byPlan: acc.byPlan,
      });
    }
  }
  return rows;
}

function prettyCategory(c: string): string {
  switch (c) {
    case 'core':
      return 'Core platform';
    case 'training':
      return 'Training';
    case 'fleet':
      return 'Fleet';
    case 'ops':
      return 'Operations';
    case 'support':
      return 'Support';
    default:
      return c.charAt(0).toUpperCase() + c.slice(1);
  }
}
